import { message } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import type { RcFile, UploadFile, UploadProps } from "antd/es/upload/interface";
import { authAxios, getEmailFromSessionStorage } from "../../utils/session_utils";
import { Dayjs } from "dayjs";

// fetch programs from backend
const fetchPrograms = async (setPrograms: Dispatch<SetStateAction<any[]>>) => {
	try {
		const url = `${process.env.REACT_APP_BACK_END_API}/auto-podder/programs`;
		const config: any = {
			method: "GET",
			url,
			headers: {
				"Content-Type": "application/json",
			},
		};
		const response = await authAxios(config);
		console.log(response.data.rows);
		const convertedObject = response.data.rows.map((item: any) => ({
			id: item.ID,
			title: `${item.TITLE} (${item.FILENAME})`,
		}));
		setPrograms(convertedObject);
	} catch (error) {
		console.error({ error });
	}
};

const useAutoPodderUploadHook = () => {
	const [loading, setLoading] = useState<boolean>(false);
	const [redirectToBatchJobs, setRedirectToBatchJobs] = useState<boolean>(false);
	const [redirectToPreSeed, setRedirectToPreSeed] = useState<string | null>(null);
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [file, setFile] = useState<any>(null);
	const [autoPodderMode, setAutoPodderMode] = useState<string>("broadcast");
	const [programs, setPrograms] = useState<Array<{ id: string; title: string }>>([]);
	const [selectedProgramId, setSelectedProgramId] = useState<string>("");
	const [weight_300, setWeight_300] = useState<string>("");
	const [weight_180, setWeight_180] = useState<string>("");
	const [weight_120, setWeight_120] = useState<string>("");
	const [weight_90, setWeight_90] = useState<string>("");
	const [weight_60, setWeight_60] = useState<string>("");
	const [weight_45, setWeight_45] = useState<string>("");
	const [weight_30, setWeight_30] = useState<string>("");
	const [weight_15, setWeight_15] = useState<string>("");
	const [max_cascade_seconds, setMax_cascade_seconds] = useState<string>("");
	const [startDate, setStartDate] = useState<Dayjs | null>(null);
	const [selectedWeekDays, setSelectedWeekDays] = useState<string[]>([]);
	// Days of week on which ads with no traffic (empty ISCI) should NOT be podded.
	// Empty = no exclusion (current behavior). Independent of selectedWeekDays.
	const [noTrafficExcludedDays, setNoTrafficExcludedDays] = useState<string[]>([]);
	// Template's earliest date-of-week index (0=Sun..6=Sat) — used to disable
	// non-matching dates in the start-date picker. The backend rejects
	// mismatches, but disabling dates up front prevents a wasted submit.
	const [requiredStartDayOfWeek, setRequiredStartDayOfWeek] = useState<number | null>(null);
	const [requiredStartDayOfWeekName, setRequiredStartDayOfWeekName] = useState<string | null>(null);
	const [templateInfoLoading, setTemplateInfoLoading] = useState<boolean>(false);

	useEffect(() => {
		setSelectedWeekDays([]);
		setNoTrafficExcludedDays([]);
		if (autoPodderMode === "syndication") {
			fetchPrograms(setPrograms);
		} else {
			// Reset syndication-specific state when switching to broadcast
			setSelectedProgramId("");
			setStartDate(null);
			setRequiredStartDayOfWeek(null);
			setRequiredStartDayOfWeekName(null);
		}
	}, [autoPodderMode]);

	// When the program changes, fetch its template info so we can constrain
	// the start-date picker and pre-select the days-of-week present in the
	// template. Reset any previously chosen startDate that no longer matches.
	useEffect(() => {
		if (autoPodderMode !== "syndication" || !selectedProgramId) {
			setRequiredStartDayOfWeek(null);
			setRequiredStartDayOfWeekName(null);
			setSelectedWeekDays([]);
			return;
		}
		(async () => {
			setTemplateInfoLoading(true);
			try {
				const resp = await authAxios({
					method: "GET",
					url: `${process.env.REACT_APP_BACK_END_API}/auto-podder/programs/${selectedProgramId}/template-info`,
					headers: { "Content-Type": "application/json" },
				});
				const dow = resp.data?.earliestDayOfWeekIndex;
				const dowName = resp.data?.earliestDayOfWeek;
				const daysOfWeek = resp.data?.daysOfWeek;
				if (typeof dow === "number") {
					setRequiredStartDayOfWeek(dow);
					setRequiredStartDayOfWeekName(dowName || null);
					setStartDate((prev) => (prev && prev.day() !== dow ? null : prev));
				}
				setSelectedWeekDays(Array.isArray(daysOfWeek) ? daysOfWeek : []);
			} catch (err) {
				console.error("Failed to fetch program template info", err);
				setRequiredStartDayOfWeek(null);
				setRequiredStartDayOfWeekName(null);
				setSelectedWeekDays([]);
			} finally {
				setTemplateInfoLoading(false);
			}
		})();
	}, [selectedProgramId, autoPodderMode]);

	const handleFileRemove = () => {
		setFile(null);
		const index = fileList.indexOf(file);
		const newFileList = fileList.slice();
		newFileList.splice(index, 1);
		setFileList(newFileList);
		return true;
	};

	const beforeUpload = (file: any) => {
		// console.log("beforeUpload", { fileList, file });
		fileList.push(file);
		setFileList(fileList);
		setFile(file);
		return false;
	};

	const uploadProps: UploadProps = {
		accept: ".csv",
		onRemove: handleFileRemove,
		beforeUpload: beforeUpload,
		fileList,
	};

	const handleUpload = async () => {
		// access input element and get value from parent component
		// const toEmailInput = document.querySelector("input[name=toEmailInput]") as HTMLInputElement;
		// const weight_300 = document.querySelector("input[name=weight_300]") as HTMLInputElement;
		// const weight_180 = document.querySelector("input[name=weight_180]") as HTMLInputElement;
		// const weight_120 = document.querySelector("input[name=weight_120]") as HTMLInputElement;
		// const weight_90 = document.querySelector("input[name=weight_90]") as HTMLInputElement;
		// const weight_60 = document.querySelector("input[name=weight_60]") as HTMLInputElement;
		// const weight_45 = document.querySelector("input[name=weight_45]") as HTMLInputElement;
		// const weight_30 = document.querySelector("input[name=weight_30]") as HTMLInputElement;
		// const weight_15 = document.querySelector("input[name=weight_15]") as HTMLInputElement;
		// const max_cascade_seconds = document.querySelector(
		// 	"input[name=max_cascade_seconds]"
		// ) as HTMLInputElement;

		if (autoPodderMode === "syndication" && selectedProgramId === "") {
			message.error("Please select a program for syndication mode");
			return;
		}

		if (autoPodderMode === "syndication" && !startDate) {
			message.error("Please select a start date to remap the structure file");
			return;
		}

		if (autoPodderMode === "syndication" && selectedWeekDays.length === 0) {
			message.error("Please select at least one week day");
			return;
		}

		const toEmail = getEmailFromSessionStorage();
		// if email is empty or domain part is not mvmediasales.com then add error
		if (toEmail?.length === 0 || !toEmail?.endsWith("mvmediasales.com")) {
			message.error("Please enter a valid mvmediasales email address");
		} else if (fileList.length !== 2 && autoPodderMode === "broadcast") {
			message.error("Please upload exactly 2 files, 1 for schedule and 1 for structure");
		} else if (fileList.length !== 1 && autoPodderMode === "syndication") {
			message.error("Please upload 1 schedule file only for syndication mode");
		} else {
			setLoading(true);
			const formData = new FormData();
			formData.append("file1", fileList[0] as RcFile);
			if (autoPodderMode === "broadcast" && fileList[1]) {
				formData.append("file2", fileList[1] as RcFile);
			}

			formData.append("toEmail", toEmail);

			if (autoPodderMode === "broadcast") {
				formData.append("weight_300", weight_300);
				formData.append("weight_180", weight_180);
				formData.append("weight_120", weight_120);
				formData.append("weight_90", weight_90);
				formData.append("weight_60", weight_60);
				formData.append("weight_45", weight_45);
				formData.append("weight_30", weight_30);
				formData.append("weight_15", weight_15);
				formData.append("max_cascade_seconds", max_cascade_seconds);
			}
			formData.append("func", "autopodder");
			formData.append("autoPodderMode", autoPodderMode);
			formData.append("programId", selectedProgramId);
			if (autoPodderMode === "syndication" && startDate) {
				formData.append("startDate", startDate.format("YYYY-MM-DD"));
				formData.append("selectedWeekDays", JSON.stringify(selectedWeekDays));
				formData.append("noTrafficExcludedDays", JSON.stringify(noTrafficExcludedDays));
			}

			try {
				const config = {
					method: "POST",
					url: `${process.env.REACT_APP_BACK_END_API}/auto-podder/upload`,
					data: formData,
					headers: {
						"Content-Type": "multipart/form-data",
					},
				};
				const response = await authAxios(config);
				if (!response.data.err) {
					message.success(response.data.message);
					setFile(null);
					setFileList([]);
					setStartDate(null);
					setSelectedProgramId("");
					// For syndication mode, redirect to pre-seeding page
					if (autoPodderMode === "syndication" && response.data.batchJobId) {
						setRedirectToPreSeed(response.data.batchJobId);
					} else {
						setRedirectToBatchJobs(true);
					}
				} else {
					message.error("Something went wrong");
				}
				setLoading(false);

				// console.log(response.data); // Handle the response from the Node.js app
			} catch (error: any) {
				setLoading(false);
				console.error(error);
				const data = error?.response?.data;
				if (data?.errorCode === "START_DATE_DOW_MISMATCH") {
					message.error(
						`Start date must be a ${data.expectedDayOfWeek} (program template starts on ${data.templateEarliestDate}). ` +
						`You picked ${data.chosenStartDate} (a ${data.actualDayOfWeek}).`,
						8
					);
				} else if (data?.error) {
					message.error(data.error);
				} else {
					message.error("Upload failed");
				}
			}
		}
	};
	return {
		loading,
		file,
		handleUpload,
		uploadProps,
		redirectToBatchJobs,
		redirectToPreSeed,
		autoPodderMode,
		setAutoPodderMode,
		programs,
		setPrograms,
		selectedProgramId,
		setSelectedProgramId,
		weight_300,
		setWeight_300,
		weight_180,
		setWeight_180,
		weight_120,
		setWeight_120,
		weight_90,
		setWeight_90,
		weight_60,
		setWeight_60,
		weight_45,
		setWeight_45,
		weight_30,
		setWeight_30,
		weight_15,
		setWeight_15,
		max_cascade_seconds,
		setMax_cascade_seconds,
		startDate,
		setStartDate,
		selectedWeekDays,
		setSelectedWeekDays,
		noTrafficExcludedDays,
		setNoTrafficExcludedDays,
		requiredStartDayOfWeek,
		requiredStartDayOfWeekName,
		templateInfoLoading,
	};
};

export default useAutoPodderUploadHook;
